/**
 * Billing endpoints.
 *
 * Org-scoped on the backend; the frontend never passes an org id — the API
 * reads `User.current_organization_id`. Account recovery lives here too since
 * it is reached from the same surfaces, and is the one unauthenticated group.
 */
import type {
  ChangePlanResponse,
  EntitlementsUsageResponse,
  Invoice,
  MySubscriptionResponse,
  PaymentMethod,
  Plan,
  ProrationPreview,
  SubscriptionDetail,
  UsageResponse,
} from '@/types/api';
import { apiClient } from './client';

/**
 * Billing API endpoints. Billing is org-scoped on the backend; the
 * frontend doesn't pass an org id - the backend reads
 * ``User.current_organization_id``.
 */
export const billingApi = {
  // ── Plans ────────────────────────────────────────────────────────────
  listPlans: () => apiClient.get<Plan[]>('/payments/plans'),

  // ── Subscription read ────────────────────────────────────────────────
  getMySubscription: () =>
    apiClient.get<MySubscriptionResponse>('/payments/subscriptions/me'),

  // ── SetupIntent + payment methods ────────────────────────────────────
  /** Mint a SetupIntent client_secret for the PaymentElement to confirm. */
  createSetupIntent: () =>
    apiClient.post<{ client_secret: string; setup_intent_id: string }>(
      '/payments/setup-intent',
      {}
    ),

  listPaymentMethods: () =>
    apiClient.get<PaymentMethod[]>('/payments/payment-methods'),

  setDefaultPaymentMethod: (paymentMethodId: string) =>
    apiClient.post<void>(
      `/payments/payment-methods/${paymentMethodId}/default`,
      {}
    ),

  deletePaymentMethod: (paymentMethodId: string) =>
    apiClient.delete<void>(`/payments/payment-methods/${paymentMethodId}`),

  // ── Subscription mutations ───────────────────────────────────────────
  /** Create the subscription. Returns the first invoice's ``client_secret``,
   *  which checkout confirms with ``stripe.confirmPayment``. ``checkout_id``
   *  keys the idempotency so a double-submitted Pay creates one. */
  createSubscription: (input: { plan_id: string; checkout_id: string }) =>
    apiClient.post<{
      subscription_id: string;
      status: string;
      client_secret: string | null;
    }>('/payments/subscriptions', input),

  /** Switch the current sub to a new plan (with proration). When the
   *  switch's charge needs 3DS, `requires_action` is set and the caller
   *  confirms `client_secret` with Stripe.js. */
  changePlan: (newPlanId: string) =>
    apiClient.patch<ChangePlanResponse>('/payments/subscriptions', {
      new_plan_id: newPlanId,
    }),

  /** Preview the proration cost before committing to a plan change.
   *  Accepts an optional `AbortSignal` so callers can enforce a tight
   *  per-request timeout (Change-plan modal currently uses 10s). */
  prorationPreview: (newPlanId: string, options?: { signal?: AbortSignal }) =>
    apiClient.get<ProrationPreview>(
      `/payments/subscriptions/proration-preview?new_plan_id=${encodeURIComponent(
        newPlanId
      )}`,
      options
    ),

  /** Schedule cancellation at period end. */
  cancelSubscription: () =>
    apiClient.post<SubscriptionDetail>('/payments/subscriptions/cancel', {}),

  /** Undo a scheduled cancel. */
  resumeSubscription: () =>
    apiClient.post<SubscriptionDetail>('/payments/subscriptions/resume', {}),

  /** Undo a scheduled at-period-end plan change (e.g. annual → monthly). */
  undoScheduledChange: () =>
    apiClient.post<SubscriptionDetail>(
      '/payments/subscriptions/undo-scheduled-change',
      {}
    ),

  /** Force-pull the subscription from Stripe into the local mirror. Called
   *  after checkout / plan change so Settings reflects the final state without
   *  depending on webhook timing. */
  refreshSubscription: () =>
    apiClient.post<MySubscriptionResponse>(
      '/payments/subscriptions/refresh',
      {}
    ),

  // ── Usage / credits + trial ──────────────────────────────────────────
  /** Current AI-credit balance + trial state, for the meter and trial UI. */
  getUsage: () => apiClient.get<UsageResponse>('/payments/usage'),

  /** Per-feature daily/monthly usage for the limit modal + usage UI. */
  getEntitlementsUsage: () =>
    apiClient.get<EntitlementsUsageResponse>('/entitlements/usage'),

  /** Start the one-time, no-card 14-day free trial. */
  startTrial: () =>
    apiClient.post<{ status: string; trial_end: string }>(
      '/payments/start-trial',
      {}
    ),

  // ── Invoices ─────────────────────────────────────────────────────────
  listInvoices: (limit = 10) =>
    apiClient.get<Invoice[]>(`/payments/invoices?limit=${limit}`),
};

/**
 * Public account-recovery endpoints (no auth required).
 */
export const accountRecoveryApi = {
  requestRestore: (email: string) =>
    apiClient.post<{ sent: boolean }>('/auth/request-restore', { email }),

  restore: (token: string) =>
    apiClient.post<{ restored: boolean; email: string }>('/auth/restore', {
      token,
    }),
};

// ── Chat conversations ─────────────────────────────────────────────────────
